import { toQueryParams } from '@/utils/to-query-params'
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

const employeesApi = createApi({
  reducerPath: 'employeesApi',
  baseQuery: fetchBaseQuery({ baseUrl: '/api/v1/employees' }),
  tagTypes: ['Employee'],

  endpoints: (builder) => ({
    getEmployees: builder.query({
      query: (searchParams = location.search) => `${searchParams}`,
      providesTags: ['Employee']
    }),

    getEmployeesSelect: builder.query({
      query: ({ q, page = 1, perPage = 10, includeHistory = false, order = {} }) => {
        const params = new URLSearchParams()
        params.set('page', page)
        params.set('per_page', perPage)
        if (q) params.set('q', q)
        if (includeHistory) params.set('include_history', 'true')
        if (order.field) params.set('order[field]', order.field)
        if (order.sort) params.set('order[sort]', order.sort)
        return `select?${params.toString()}`
      },
      // la respuesta del controlador select es: { data: [...], meta: {...} }
      transformResponse: (response) => {
        // normaliza por si tu controlador devuelve un array plano
        if (Array.isArray(response)) return { data: response, meta: {} }
        const { meta = {}, ...rest } = response || {}
        const data = rest.data || rest || []
        return { data, meta }
      },
      providesTags: ['Employee']
    }),

    getEmployee: builder.query({
      query: ({ id }) => `${id}`,
      transformResponse: (response) => response.employee,
      providesTags: (result, error, id) =>
        result ? [{ type: 'Employee', id }] : [],
    }),

    getEmployeeAttachments: builder.query({
      query: ({
        id,
        perPage,
        recents = false,
        periodYear,
        periodMonth,
        attachmentTypeId,
        employeeContractId
      }) => {
        const params = toQueryParams({
          perPage,
          recents,
          periodYear,
          periodMonth,
          attachmentTypeId,
          employeeContractId
        })
        return `${id}/attachments?` + params
      },
      transformResponse: (response) => response.employees
    }),

    createEmployee: builder.mutation({
      query: (body) => ({
        method: 'POST',
        body: { employee: body }
      }),
      invalidatesTags: ['Employee']
    }),

    updateEmployee: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `${id}`,
        method: 'PATCH',
        body: { employee: body }
      }),
      invalidatesTags: ['Employee']
    }),

    deleteEmployee: builder.mutation({
      query: (id) => ({
        url: `${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) =>
        error ? [] : [{ type: 'Employee', id }]
    }),
  })
})

const {
  useGetEmployeesQuery,
  useGetEmployeesSelectQuery,
  useGetEmployeeAttachmentsQuery,
  useGetEmployeeQuery,
  useCreateEmployeeMutation,
  useUpdateEmployeeMutation,
  useDeleteEmployeeMutation
} = employeesApi

export {
  useGetEmployeesQuery,
  useGetEmployeesSelectQuery,
  useGetEmployeeAttachmentsQuery,
  useGetEmployeeQuery,
  useCreateEmployeeMutation,
  useUpdateEmployeeMutation,
  useDeleteEmployeeMutation,
  employeesApi
}